import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Box, Button, Card, CardContent, CardMedia, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Tab, Tabs, Typography } from "@mui/material";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";

import {
  addCoreNetworkMessage,
  acceptCoreNetworkOffer,
  approveCoreNetworkPayout,
  cancelCoreNetworkOffer,
  cancelCoreNetworkPayout,
  cancelCoreNetworkWorkRequest,
  createCoreNetworkMarketplaceFee,
  createCoreNetworkSubmission,
  createOrUpdateCoreNetworkPayout,
  createCoreNetworkRating,
  declineCoreNetworkOffer,
  generateCoreNetworkMatches,
  getCoreNetworkMemberRatingSummary,
  getCoreNetworkMessageAttachment,
  getCoreNetworkWorkRequest,
  getCoreNetworkTalentAssignmentPreview,
  listCoreNetworkEvents,
  listCoreNetworkMatches,
  listCoreNetworkMessages,
  listCoreNetworkOffers,
  listCoreNetworkProviderStatusHistory,
  listCoreNetworkRatings,
  markCoreNetworkMessagesRead,
  markCoreNetworkPayoutPaid,
  reviewCoreNetworkSubmission,
  sendCoreNetworkOffers,
  selectCoreNetworkOffer,
  shortlistCoreNetworkMatches,
  uploadCoreNetworkMessageAttachments,
} from "../../../services/coreNetwork";
import { AuthContext } from "../../../helpers/AuthContext";
import { CORE_NETWORK_PERMISSIONS, hasAnyCoreNetworkPermission, hasCoreNetworkPermission } from "../../../helpers/coreNetworkPermissions";
import {
  CoreNetworkShell,
  CORE_NETWORK_LABEL,
  ErrorState,
  KeyValueGrid,
  LoadingState,
  SectionCard,
  StatusChip,
  formatCurrency,
  formatDateTime,
  normalizeJsonArray,
  normalizeJsonObject,
  safeArray,
  toTitleCase,
} from "./coreNetworkUiShared";
import CoreNetworkMatchPanel from "./components/CoreNetworkMatchPanel";
import CoreNetworkOfferPanel from "./components/CoreNetworkOfferPanel";
import CoreNetworkMessageThread from "./components/CoreNetworkMessageThread";
import CoreNetworkSubmissionPanel from "./components/CoreNetworkSubmissionPanel";
import CoreNetworkAssignmentPanel from "./components/CoreNetworkAssignmentPanel";
import NetworkPortalAssignmentDetail from "../../network/NetworkPortalAssignmentDetail";
import CoreNetworkRatingPanel from "./components/CoreNetworkRatingPanel";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { resolveProductImage } from "../../../utils/productImage";


export function getCoreNetworkDocumentContextLink({ instanceUniqueId, documentType, documentId, orderId } = {}) {
  const normalizedDocumentType = String(documentType || "").trim().toLowerCase();
  const normalizedDocumentId = String(documentId || "").trim();
  const normalizedOrderId = String(orderId || "").trim();
  if (normalizedDocumentType !== "sales-order" || !normalizedDocumentId || !normalizedOrderId || !instanceUniqueId) {
    return null;
  }
  return `/instance/${instanceUniqueId}/sales/orders/${normalizedOrderId}/sales-order/${normalizedDocumentId}`;
}

export function CoreNetworkProductImage({ product }) {
  const imageUrl = resolveProductImage({ ...product, product });
  const [failed, setFailed] = useState(false);
  if (!imageUrl || failed) {
    return (
      <Box sx={{ height: 140, bgcolor: "grey.50", borderRadius: 1, display: "grid", placeItems: "center", color: "text.secondary" }} role="img" aria-label="Product image unavailable">
        <Stack alignItems="center" spacing={0.5}><ImageOutlinedIcon /><Typography variant="caption">Product image unavailable</Typography></Stack>
      </Box>
    );
  }
  return <Box component="img" src={imageUrl} alt={`${product?.name || "Product"} product`} onError={() => setFailed(true)} sx={{ width: "100%", height: 140, objectFit: "contain", bgcolor: "grey.50", borderRadius: 1, display: "block" }} />;
}

function hasDisplayValue(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0 && value.trim() !== "-";
  return true;
}

function compactKeyValueItems(items = []) {
  return safeArray(items).filter((item) => hasDisplayValue(item?.value));
}

export default function CoreNetworkWorkRequestDetail() {
  const { instanceUniqueId, id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { authState } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [busyOfferId, setBusyOfferId] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [workRequest, setWorkRequest] = useState(null);
  const [matches, setMatches] = useState([]);
  const [offers, setOffers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [providerStatusHistory, setProviderStatusHistory] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [talentPreviewOpen, setTalentPreviewOpen] = useState(false);
  const [talentPreviewLoading, setTalentPreviewLoading] = useState(false);
  const [talentPreviewData, setTalentPreviewData] = useState(null);
  const [talentPreviewError, setTalentPreviewError] = useState("");
  const previewRoles = []
    .concat(authState?.roles || [])
    .concat(authState?.role ? [authState.role] : [])
    .concat(authState?.user?.roles || [])
    .concat(authState?.user?.role ? [authState.user.role] : [])
    .map((role) => String(role || "").trim().toLowerCase());
  const canPreviewTalent = previewRoles.some((role) => ["administrator", "admin", "super-administrator"].includes(role));
  const canManagePayment = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_PAYMENT, instanceUniqueId);
  const canViewPlacement = hasAnyCoreNetworkPermission(
    authState,
    [CORE_NETWORK_PERMISSIONS.MANAGE_BADGES, CORE_NETWORK_PERMISSIONS.MANAGE_SUBSCRIPTIONS],
    instanceUniqueId
  );
  const canGenerateMatches = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.GENERATE_MATCHES, instanceUniqueId);
  const canShortlistMatches = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.ASSIGN_REQUEST, instanceUniqueId);
  const canSendOffers = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.SEND_OFFERS, instanceUniqueId);
  const canMessage = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MESSAGE, instanceUniqueId);
  const canReviewSubmission = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.REVIEW_SUBMISSION, instanceUniqueId);
  const canRateProvider = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.RATE_PROVIDER, instanceUniqueId);

  const loadDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [requestResponse, matchesResponse, offersResponse, messagesResponse, eventsResponse, ratingsResponse, providerStatusResponse] = await Promise.all([
        getCoreNetworkWorkRequest(id, instanceUniqueId),
        listCoreNetworkMatches(id, { instanceUniqueId }),
        listCoreNetworkOffers(id, { instanceUniqueId }),
        listCoreNetworkMessages(id, { instanceUniqueId, includeInternal: true }),
        listCoreNetworkEvents(id, { instanceUniqueId }),
        listCoreNetworkRatings(id, { instanceUniqueId }),
        listCoreNetworkProviderStatusHistory(id, { instanceUniqueId }),
      ]);

      const nextWorkRequest = requestResponse.workRequest || null;
      setWorkRequest(nextWorkRequest);
      setMatches(safeArray(matchesResponse.matches));
      setOffers(safeArray(offersResponse.offers));
      setMessages(safeArray(messagesResponse.messages));
      setMessageUnreadCount(Number(messagesResponse.unreadCount || 0));
      setEvents(safeArray(eventsResponse.events));
      setProviderStatusHistory(safeArray(providerStatusResponse.history));
      const nextRatings = safeArray(ratingsResponse.ratings);
      setRatings(nextRatings);
      setSelectedMatchIds([]);

      const completedAssignments = safeArray(nextWorkRequest?.assignments).filter(
        (assignment) => String(assignment.status || "").toLowerCase() === "completed"
      );
      const primaryMemberId = completedAssignments[0]?.memberId;
      if (primaryMemberId) {
        const summaryResponse = await getCoreNetworkMemberRatingSummary(primaryMemberId, instanceUniqueId);
        setRatingSummary(summaryResponse.summary || null);
      } else {
        setRatingSummary(null);
      }
    } catch (loadError) {
      setError(loadError?.response?.data?.message || loadError.message || "Unable to load work request.");
    } finally {
      setLoading(false);
    }
  }, [id, instanceUniqueId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const revisionHistory = useMemo(() => normalizeJsonArray(workRequest?.revisionHistoryJson), [workRequest]);
  const files = useMemo(() => normalizeJsonArray(workRequest?.filesJson), [workRequest]);
  const payouts = useMemo(() => safeArray(workRequest?.payouts), [workRequest]);
  const marketplaceFees = useMemo(() => safeArray(workRequest?.marketplaceFees), [workRequest]);

  const toggleSelectedMatch = (matchId) => {
    setSelectedMatchIds((prev) =>
      prev.includes(matchId) ? prev.filter((idValue) => idValue !== matchId) : [...prev, matchId]
    );
  };

  const runAction = async (actionKey, callback) => {
    try {
      setBusyAction(actionKey);
      const result = await callback();
      await loadDetail();
      return result;
    } catch (actionError) {
      setError(actionError?.response?.data?.message || actionError.message || "Action failed.");
    } finally {
      setBusyAction("");
    }
  };

  const openTalentPreview = async (assignmentId) => {
    if (!canPreviewTalent) return;
    setTalentPreviewOpen(true);
    setTalentPreviewData(null);
    setTalentPreviewError("");
    setTalentPreviewLoading(true);
    try {
      const response = await getCoreNetworkTalentAssignmentPreview(id, assignmentId, instanceUniqueId);
      setTalentPreviewData(response.preview || null);
      if (!response.preview) setTalentPreviewError("The Talent assignment preview is unavailable.");
    } catch (previewError) {
      setTalentPreviewError(previewError?.response?.data?.message || previewError.message || "Unable to load Talent preview.");
    } finally {
      setTalentPreviewLoading(false);
    }
  };

  const handleOfferAction = async (offer, fn) => {
    try {
      setBusyOfferId(offer.id);
      await fn();
      await loadDetail();
    } catch (offerError) {
      setError(offerError?.response?.data?.message || offerError.message || "Offer action failed.");
    } finally {
      setBusyOfferId(null);
    }
  };

  const handlePaymentAction = async (callback) => {
    try {
      setPaymentSaving(true);
      await callback();
      await loadDetail();
    } catch (paymentError) {
      setError(paymentError?.response?.data?.message || paymentError.message || "Payment action failed.");
    } finally {
      setPaymentSaving(false);
    }
  };

  const detail = workRequest?.detail || {};
  const detailFiles = safeArray(detail?.artwork?.files);
  const sourceFiles = detailFiles.length ? detailFiles : files;
  const requestDescription = String(detail.description || workRequest?.description || "");
  const requestInstructions = String(detail.instructions || workRequest?.instructions || "");
  const showInstructions = requestInstructions.trim() && requestInstructions.trim() !== requestDescription.trim();
  const documentType = detail.order?.documentType || workRequest?.documentType || "";
  const documentId = detail.order?.documentId || workRequest?.documentId || "";
  const documentOrderId = detail.order?.orderId || workRequest?.orderId || "";
  const documentContextText = documentType || documentId ? `${documentType || "-"} / ${documentId || "-"}` : "-";
  const documentContextLink = getCoreNetworkDocumentContextLink({
    instanceUniqueId,
    documentType,
    documentId,
    orderId: documentOrderId,
  });
  const documentContextValue = documentContextLink ? (
    <Link
      to={documentContextLink}
      aria-label={`Open sales order ${documentId}`}
      style={{ color: "#103a71", fontWeight: 700, textDecoration: "none" }}
    >
      {`Sales Order ${documentId}`}
    </Link>
  ) : documentContextText;

  const overviewItems = compactKeyValueItems([
    { label: "Request type", value: toTitleCase(detail.requestType || workRequest?.requestType) },
    { label: "Source module", value: detail.sourceModule || workRequest?.sourceModule ? toTitleCase(detail.sourceModule || workRequest?.sourceModule) : null },
    { label: "Project / Order", value: detail.order?.orderId || workRequest?.orderId || null },
    { label: "Sales order", value: detail.order?.salesOrderId || null },
    { label: "Purchase order", value: detail.order?.purchaseOrderNumber || detail.order?.purchaseOrderId || workRequest?.poId || null },
    { label: "Production job", value: detail.order?.productionJobId || workRequest?.productionJobId || null },
    { label: "Document context", value: documentContextLink ? documentContextValue : documentType || documentId ? documentContextText : null },
    { label: "Provider status", value: detail.providerStatus ? toTitleCase(detail.providerStatus) : null },
    ...(canManagePayment ? [{ label: "Budget", value: Number.isFinite(Number(workRequest?.budgetAmount)) ? formatCurrency(workRequest?.budgetAmount, workRequest?.budgetCurrency || "CAD") : null }] : []),
    { label: "Deadline", value: detail.deadlineAt || workRequest?.deadlineAt ? formatDateTime(detail.deadlineAt || workRequest?.deadlineAt) : null },
    { label: "Requester", value: detail.requester?.name || detail.requester?.email || null },
    { label: "Last activity", value: detail.lastActivityAt || workRequest?.lastActivityAt ? formatDateTime(detail.lastActivityAt || workRequest?.lastActivityAt) : null },
  ]);

  const tabDefinitions = [
    { value: "overview", label: "Overview" },
    { value: "request", label: "Request Details" },
    { value: "assignments", label: "Assignments", count: safeArray(workRequest?.assignments).length },
    { value: "messages", label: "Messages", count: messages.length },
    { value: "submissions", label: "Submissions & Review", count: safeArray(workRequest?.submissions).length + revisionHistory.length },
  ];
  const tabValues = tabDefinitions.map((tab) => tab.value);
  const queryTab = new URLSearchParams(location.search).get("tab") || "overview";
  const activeTab = tabValues.includes(queryTab) ? queryTab : "overview";
  const handleTabChange = (_event, nextTab) => {
    const params = new URLSearchParams(location.search);
    if (nextTab === "overview") params.delete("tab");
    else params.set("tab", nextTab);
    history.push({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : "" });
  };

  function renderFileCards(items, emptyText) {
    const normalizedItems = safeArray(items);
    if (!normalizedItems.length) {
      return <Typography variant="body2" color="text.secondary">{emptyText}</Typography>;
    }
    return (
      <Grid container spacing={2}>
        {normalizedItems.map((file, index) => {
          const href = file.downloadUrl || file.previewUrl || file.url || file.fileName || "";
          const label = file.fileName || file.name || file.url || `File ${index + 1}`;
          const isImage = /\.(png|jpe?g|gif|webp|svg)(\?|#|$)/i.test(href);
          return (
            <Grid item xs={12} sm={6} md={4} key={`${label}-${href || index}`}>
              <Card elevation={0} sx={{ height: "100%", border: "1px solid #edf1f7", borderRadius: 2, overflow: "hidden" }}>
                {href && isImage ? <CardMedia component="img" image={href} alt={label} sx={{ height: 150, objectFit: "contain", backgroundColor: "#f8fafc" }} /> : null}
                <CardContent sx={{ p: 1.5 }}>
                  <Stack spacing={0.75}>
                    <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>{label}</Typography>
                    {file.placementName || file.placementCode ? <Typography variant="caption" color="text.secondary">{[file.placementName, file.placementCode].filter(Boolean).join(" · ")}</Typography> : null}
                    {file.description ? <Typography variant="caption" color="text.secondary">{file.description}</Typography> : null}
                    {href ? <Button size="small" variant="outlined" href={href} target="_blank" rel="noopener noreferrer">Open file</Button> : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  }

  return (
    <CoreNetworkShell
      title="Core Network Work Request Detail"
      breadcrumb={`${CORE_NETWORK_LABEL} / Work Requests / Detail`}
    >
      {loading ? <LoadingState label="Loading work request detail..." /> : null}
      {!loading && error ? <ErrorState message={error} onAction={loadDetail} /> : null}
      {!loading && !error && workRequest ? (
        <Stack spacing={3}>
          <SectionCard
            title={detail.title || workRequest.title || `Work Request #${workRequest.id}`}
            subtitle={`${toTitleCase(workRequest.requestType)} | ${toTitleCase(workRequest.fulfillmentProvider)}`}
            action={
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={loadDetail}>Refresh</Button>
                <Button variant="outlined" color="error" onClick={() => runAction("cancel-request", () => cancelCoreNetworkWorkRequest(workRequest.id, { instanceUniqueId, reason: "Cancelled from internal UI" }))}>
                  Cancel request
                </Button>
              </Stack>
            }
          >
            <Stack spacing={2}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Core Network work request detail tabs"
              >
                {tabDefinitions.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    label={tab.count ? <Badge color="primary" badgeContent={tab.count}>{tab.label}</Badge> : tab.label}
                  />
                ))}
              </Tabs>
            </Stack>
          </SectionCard>

          {activeTab === "overview" ? (
            <SectionCard title="Overview" subtitle="Operational summary and request context.">
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <StatusChip value={detail.status || workRequest.status} />
                  <StatusChip value={detail.fulfillment?.networkType || workRequest.networkType} />
                  <StatusChip value={detail.fulfillment?.provider || workRequest.fulfillmentProvider} />
                  {detail.providerStatus ? <StatusChip value={detail.providerStatus} /> : null}
                </Stack>
                <KeyValueGrid items={overviewItems} />
                {requestDescription ? (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    <strong>Description:</strong> {requestDescription}
                  </Typography>
                ) : null}
                {showInstructions ? (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    <strong>Instructions:</strong> {requestInstructions}
                  </Typography>
                ) : null}
              </Stack>
            </SectionCard>
          ) : null}

          {activeTab === "request" ? (
            <Stack spacing={3}>
              <SectionCard title="Products" subtitle="Product and SKU details from the submitted request.">
                {safeArray(detail.products).length ? (
                  <Grid container spacing={2}>
                    {safeArray(detail.products).map((product, index) => (
                      <Grid item xs={12} md={6} key={`${product.sku || product.name || index}`}>
                        <Card elevation={0} sx={{ height: "100%", border: "1px solid #edf1f7", borderRadius: 2 }}>
                          <CardContent>
                            <Stack spacing={1.5}>
                              <CoreNetworkProductImage product={product} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{product.name || "Product"}</Typography>
                              <KeyValueGrid
                                items={[
                                  { label: "SKU", value: product.sku || "-" },
                                  { label: "Garment / Variant", value: product.garment || "-" },
                                  { label: "Category", value: product.category || "-" },
                                  { label: "Priority", value: product.priority || detail.priority || workRequest.priority || "-" },
                                  { label: "Product URL", value: product.productUrl || "-" },
                                ]}
                              />
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">No structured product details are available for this work request.</Typography>
                )}
              </SectionCard>

              <SectionCard title="Artwork and decoration" subtitle="Placement-level artwork requirements.">
                {safeArray(detail.artwork?.placements).length ? (
                  <Grid container spacing={2}>
                    {safeArray(detail.artwork.placements).map((placement, index) => (
                      <Grid item xs={12} md={6} key={`${placement.placementCode || placement.placementName || index}`}>
                        <Card elevation={0} sx={{ height: "100%", border: "1px solid #edf1f7", borderRadius: 2 }}>
                          <CardContent>
                            <Stack spacing={1.5}>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {placement.decorationType ? <StatusChip value={placement.decorationType} /> : null}
                                {placement.processType ? <StatusChip value={placement.processType} /> : null}
                                {placement.outputFormat ? <StatusChip value={placement.outputFormat} /> : null}
                              </Stack>
                              <KeyValueGrid
                                items={[
                                  { label: "Artwork", value: placement.description || "-" },
                                  { label: "Product", value: placement.productName || "-" },
                                  { label: "SKU", value: placement.sku || "-" },
                                  { label: "Placement", value: placement.placementName || "-" },
                                  { label: "Placement code", value: placement.placementCode || "-" },
                                  { label: "Dimensions", value: placement.width && placement.height ? `${placement.width} × ${placement.height} ${placement.measurementUnit || ""}`.trim() : "-" },
                                  { label: "Colours", value: safeArray(placement.colors).join(", ") || "-" },
                                  { label: "Change request", value: placement.changeRequest === true ? "Yes" : placement.changeRequest === false ? "No" : "-" },
                                  { label: "Placement instructions", value: placement.instructions || "-" },
                                ]}
                              />
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">No structured artwork placements are available for this work request.</Typography>
                )}
              </SectionCard>

              <SectionCard title="Source artwork" subtitle="Normalized source files from uploaded files and nested Smart Send artwork URLs.">
                {renderFileCards(sourceFiles, "No source artwork files were found for this work request.")}
              </SectionCard>

              <SectionCard title="Purchase-order context">
                <KeyValueGrid
                  items={[
                    { label: "Project / Order", value: detail.order?.orderId || workRequest.orderId || "-" },
                    { label: "Sales order", value: detail.order?.salesOrderId || "-" },
                    { label: "Selected purchase-order ID", value: detail.order?.purchaseOrderId || workRequest.poId || "-" },
                    { label: "Purchase-order number", value: detail.order?.purchaseOrderNumber || "-" },
                    { label: "Document context", value: documentContextValue },
                  ]}
                />
              </SectionCard>
            </Stack>
          ) : null}

          {activeTab === "assignments" ? (
            <CoreNetworkAssignmentPanel assignments={safeArray(workRequest.assignments)} canViewPayment={canManagePayment} onPreviewTalent={canPreviewTalent ? openTalentPreview : null} />
          ) : null}

          {activeTab === "messages" ? (
            <CoreNetworkMessageThread
              messages={messages}
              sending={busyAction === "message"}
              uploading={busyAction === "message-upload"}
              unreadCount={messageUnreadCount}
              onSendMessage={canMessage ? ((payload) => runAction("message", () => addCoreNetworkMessage(workRequest.id, { ...payload, instanceUniqueId }))) : null}
              onUploadAttachments={canMessage ? ((files) => runAction("message-upload", () => uploadCoreNetworkMessageAttachments(workRequest.id, files, { instanceUniqueId }))) : null}
              onMarkRead={canMessage ? (() => runAction("message-read", () => markCoreNetworkMessagesRead(workRequest.id, { instanceUniqueId, includeInternal: true }))) : null}
              onOpenAttachment={async (message, attachment) => {
                const response = await getCoreNetworkMessageAttachment(message.id, attachment.fileId, { instanceUniqueId });
                const url = response?.attachment?.downloadUrl || response?.attachment?.previewUrl;
                if (url) window.open(url, "_blank", "noopener,noreferrer");
              }}
            />
          ) : null}

          {activeTab === "submissions" ? (
            <Stack spacing={3}>
              {canPreviewTalent && safeArray(workRequest.assignments).length ? (
                <SectionCard title="View as Talent" subtitle="Inspect the exact assignment experience without changing the designer's work.">
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {safeArray(workRequest.assignments).filter((assignment) => assignment.memberId).map((assignment) => (
                      <Button
                        key={assignment.id}
                        variant="outlined"
                        onClick={() => openTalentPreview(assignment.id)}
                      >
                        View {assignment.member?.displayName || `Talent #${assignment.memberId}`}'s assignment
                      </Button>
                    ))}
                  </Stack>
                </SectionCard>
              ) : null}
              <SectionCard title="Revision history" noPadding>
                <Box sx={{ p: 3 }}>
                  {revisionHistory.length ? (
                    <Stack spacing={1.5}>{revisionHistory.map((entry, index) => (
                      <Stack key={`${entry.requestedAt || "rev"}-${index}`} spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Revision {index + 1}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDateTime(entry.requestedAt)}</Typography>
                        <Typography variant="body2">{entry.note || "No note recorded"}</Typography>
                      </Stack>
                    ))}</Stack>
                  ) : <Typography variant="body2" color="text.secondary">No revisions have been requested yet.</Typography>}
                </Box>
              </SectionCard>
              <CoreNetworkSubmissionPanel
                submissions={safeArray(workRequest.submissions)}
                creating={busyAction === "submission"}
                reviewingId={reviewingId}
                onCreateSubmission={canMessage ? ((payload) => runAction("submission", () => createCoreNetworkSubmission(workRequest.id, { ...payload, instanceUniqueId }))) : null}
                onReviewSubmission={canReviewSubmission ? (async (submission, status, reviewNotes) => {
                  try {
                    setReviewingId(submission.id);
                    await reviewCoreNetworkSubmission(submission.id, {
                      instanceUniqueId,
                      status,
                      notes: reviewNotes || `Reviewed from internal UI as ${status}`,
                      workRequestStatus: status === "approved" ? "ready" : undefined,
                    });
                    await loadDetail();
                  } catch (reviewError) {
                    setError(reviewError?.response?.data?.message || reviewError.message || "Review failed.");
                  } finally {
                    setReviewingId(null);
                  }
                }) : null}
              />
              <CoreNetworkRatingPanel
                assignments={safeArray(workRequest.assignments)}
                ratings={ratings}
                ratingSummary={ratingSummary}
                creating={busyAction === "rating"}
                onCreateRating={canRateProvider ? ((payload) => runAction("rating", () => createCoreNetworkRating(workRequest.id, { ...payload, instanceUniqueId }))) : null}
              />
            </Stack>
          ) : null}

        </Stack>
      ) : null}
      <Dialog
        open={talentPreviewOpen}
        onClose={() => setTalentPreviewOpen(false)}
        maxWidth="xl"
        fullWidth
        aria-labelledby="core-network-talent-preview-title"
        PaperProps={{ sx: { maxHeight: "92vh", borderRadius: { xs: 1.5, sm: 3 }, overflow: "hidden" } }}
      >
        <DialogTitle id="core-network-talent-preview-title" sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
            <Typography component="span" variant="h6" sx={{ fontWeight: 800 }}>Talent assignment preview</Typography>
            <Chip size="small" variant="outlined" label="Read only" />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            See the assignment as the designer sees it, without accessing their account.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#f5f7fb", minHeight: "60vh", px: { xs: 2, md: 3 }, py: 2.5 }}>
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            This preview is read-only: actions and uploads are disabled, and opening it does not mark Talent messages as read.
          </Alert>
          {talentPreviewLoading ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 6 }}><CircularProgress /><Typography>Loading Talent view...</Typography></Stack>
          ) : talentPreviewError ? (
            <Alert severity="error">{talentPreviewError}</Alert>
          ) : talentPreviewData ? (
            <NetworkPortalAssignmentDetail previewData={talentPreviewData} previewInstanceUniqueId={instanceUniqueId} />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
          <Button variant="outlined" onClick={() => setTalentPreviewOpen(false)}>Close preview</Button>
        </DialogActions>
      </Dialog>
    </CoreNetworkShell>
  );
}
